import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

/**
 * Custom hook to manage dropdown filters.
 * Maps filters with `enabled` flag, handles visibility toggling, and checks filter visibility.
 *
 * @param {Object} params - Hook parameters.
 * @param {Array<{id: string, visible: boolean, [key: string]: any}>} params.filters - Array of filter objects from the slice.
 * @param {function(string): boolean} params.isFilterEnabledFn - Function that takes a filter ID and returns if it is enabled (permission logic).
 * @param {function(Object): Object} params.toggleFiltersFn - Redux action creator to toggle a filter visibility.
 */
export const useDropdownFilters = ({ filters, isFilterEnabledFn, toggleFiltersFn }) => {
	const dispatch = useDispatch();

	/**
	 * Map filters with enabled flag based on `isFilterEnabledFn`
	 * @type {Array<(*&{enabled: boolean})>}
	 */
	const dropdownFilters = useMemo(() => {
		if (!filters) return [];
		return filters.map((f) => ({
			...f,
			enabled: isFilterEnabledFn(f.id),
		}));
	}, [filters, isFilterEnabledFn]);

	// Toggle the visibility of filter input
	const toggleFilter = useCallback(
		(filterId) => dispatch(toggleFiltersFn({ filterId })),
		[dispatch],
	);

	// Check filter visibility state
	const isFilterVisible = useCallback(
		(filterId) => filters?.some((f) => f?.id === filterId && f?.visible),
		[filters],
	);

	const isDropdownFilterVisible = useMemo(
		() => dropdownFilters?.some((f) => f.enabled) ?? false,
		[filters],
	);

	return {
		dropdownFilters,
		isDropdownFilterVisible,
		toggleFilter,
		isFilterVisible,
	};
};
